import { useState, useCallback, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, X, Check, Move, RectangleHorizontal, RectangleVertical } from "lucide-react";

// Visible magnet area ratio: 78 x 53 mm. Portrait flips it (53 x 78).
const ASPECT_LANDSCAPE = 78 / 53;
const ASPECT_PORTRAIT = 53 / 78;
// Allows zooming out to 50% so customers can leave a safety margin around the photo.
const MIN_ZOOM = 0.5;

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });
}

// Renders the exact visible crop to a canvas -> dataURL for a WYSIWYG thumbnail.
// Output is capped in width so it stays small enough for sessionStorage persistence.
async function buildThumbnail(imageSrc, pixelCrop, maxW = 500) {
  const image = await createImage(imageSrc);
  const scale = Math.min(1, maxW / Math.max(1, pixelCrop.width));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(pixelCrop.width * scale));
  canvas.height = Math.max(1, Math.round(pixelCrop.height * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height
  );
  return canvas.toDataURL("image/jpeg", 0.85);
}

// Largest box of the given aspect ratio that fits inside a containerW x containerH area.
function fitBox(containerW, containerH, aspect) {
  if (containerW > containerH * aspect) return { width: containerH * aspect, height: containerH };
  return { width: containerW, height: containerW / aspect };
}

export const CropModal = ({ image, filename, initialArea = null, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(initialArea); // percentages
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orientation, setOrientation] = useState(initialArea?.orientation || "landscape");
  const [autoSet, setAutoSet] = useState(!!initialArea?.orientation);
  const stageRef = useRef(null);
  // Explicit crop-frame size in px, derived from the stage container so the frame always
  // fills the available space (react-easy-crop's own auto-sizing is limited to the
  // rendered photo size, which is tiny whenever the photo's aspect doesn't match the mask).
  const [cropSize, setCropSize] = useState(null);

  // Auto-detect orientation from the source photo (portrait photo -> portrait mask),
  // unless the customer is re-editing an existing crop that already has an orientation.
  useEffect(() => {
    if (autoSet) return;
    const img = new Image();
    img.onload = () => {
      setOrientation(img.naturalHeight > img.naturalWidth ? "portrait" : "landscape");
      setAutoSet(true);
    };
    img.src = image;
  }, [image, autoSet]);

  const aspect = orientation === "portrait" ? ASPECT_PORTRAIT : ASPECT_LANDSCAPE;

  // Recompute the crop-frame size whenever the stage resizes or the orientation changes.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width && rect.height) setCropSize(fitBox(rect.width, rect.height, aspect));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [aspect]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setArea(croppedArea);
    setAreaPixels(croppedAreaPixels);
  }, []);

  const switchOrientation = (next) => {
    if (next === orientation) return;
    setOrientation(next);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleConfirm = async () => {
    if (!area || !areaPixels) return;
    setBusy(true);
    try {
      const thumbnail = await buildThumbnail(image, areaPixels);
      onConfirm({
        crop: {
          x: area.x,
          y: area.y,
          width: area.width,
          height: area.height,
          orientation,
        },
        thumbnail,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
      data-testid="crop-modal"
    >
      <div
        className={`bg-[#141416] border border-white/10 rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col transition-[max-width] duration-300 ${
          orientation === "portrait"
            ? "max-w-md h-[92vh] max-h-[880px]"
            : "max-w-2xl h-[88vh] max-h-[820px]"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
          <div>
            <h3 className="font-bold text-white text-sm">Foto zuschneiden</h3>
            <p className="text-xs text-gray-400 truncate max-w-[240px]">{filename}</p>
          </div>
          <button
            onClick={onCancel}
            data-testid="crop-cancel-x"
            className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Crop stage – fixed 78:53 mask, zoom & pan only. flex-1 gives the photo maximum space. */}
        <div className="relative bg-black flex-1 min-h-0" ref={stageRef}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            minZoom={MIN_ZOOM}
            maxZoom={4}
            aspect={aspect}
            cropSize={cropSize || undefined}
            showGrid={true}
            objectFit="contain"
            restrictPosition={false}
            initialCroppedAreaPercentages={initialArea || undefined}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { backgroundColor: "#000" },
              mediaStyle: { backgroundColor: "#000" },
              cropAreaStyle: {
                border: "2px solid rgba(255,255,255,0.95)",
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
              },
            }}
          />
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[11px] text-white/70 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full pointer-events-none whitespace-nowrap">
            <Move className="w-3 h-3" />
            Verschieben zum Positionieren
          </div>
        </div>

        <div className="px-5 py-3 space-y-3 flex-shrink-0 border-t border-white/10">
          {/* Orientation toggle: Querformat / Hochformat */}
          <div className="flex items-center gap-2 justify-center" data-testid="crop-orientation-toggle">
            <button
              type="button"
              onClick={() => switchOrientation("landscape")}
              data-testid="crop-orientation-landscape"
              className={`flex items-center gap-2 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                orientation === "landscape"
                  ? "border-[#E31E24] bg-[#E31E24]/15 text-[#E31E24]"
                  : "border-white/15 text-gray-400 hover:border-white/30"
              }`}
            >
              <RectangleHorizontal className="w-4 h-4" /> Querformat
            </button>
            <button
              type="button"
              onClick={() => switchOrientation("portrait")}
              data-testid="crop-orientation-portrait"
              className={`flex items-center gap-2 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                orientation === "portrait"
                  ? "border-[#E31E24] bg-[#E31E24]/15 text-[#E31E24]"
                  : "border-white/15 text-gray-400 hover:border-white/30"
              }`}
            >
              <RectangleVertical className="w-4 h-4" /> Hochformat
            </button>
          </div>

          {/* Zoom control */}
          <div className="flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="range"
              min={MIN_ZOOM}
              max={4}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="crop-zoom-slider"
              className="w-full accent-[#E31E24]"
              aria-label="Zoom"
            />
            <ZoomIn className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid="crop-cancel-btn"
              className="flex-1 h-11 rounded-full border-2 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={busy || !area}
              data-testid="crop-confirm-btn"
              className="flex-1 h-11 rounded-full bg-[#E31E24] hover:bg-red-700 text-white font-semibold"
            >
              <Check className="w-5 h-5 mr-2" />
              Übernehmen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
